"use client";
/**
 * Group of (styled) components used for pop-up menus.
 *
 * Usage:
 * <PopupMenu close={() => {this.closeMenu()}}>
 *  <MenuHeader>First Section</MenuHeader>
 *  <MenuItem>
 *    <Link to="/first-thing/">First Thing</Link>
 *  <MenuItem>
 * </PopupMenu>
 *
 */

import React, {
  useLayoutEffect,
  useEffect,
  useState,
  useCallback,
  useRef,
  MouseEventHandler,
  forwardRef,
} from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";

import useWindowSize from "../../hooks/useWindowSize";

import Box, { BoxProps } from "../Box";
import Flex from "../Flex";
import factory from "../../testUtils/factory";
import { settings } from "shared";
import useIsClient from "../../hooks/useIsClient";

export type MenuProps = {
  source: string;
  open?: boolean;
  vPos?: "alignTop" | "alignBottom" | "center" | "floatAbove" | "floatBelow";
  hPos?: "alignLeft" | "alignRight" | "center" | "floatLeft" | "floatRight";
  width?: string | number;
  maxHeight?: string | number;
  close: () => void;
  stayOpenOnClick?: boolean;
  clickAwayClose?: boolean;
  children?: React.ReactNode;
  zIndex?: string | number;
  "data-testid"?: string;
};

type PositionData = {
  width?: number;
  top?: string | number;
  bottom?: string | number;
  left?: string | number;
  right?: string | number;
  transform?: string;
};

export const MenuContainer = styled(Flex).attrs<BoxProps>(props => {
  return {
    maxWidth: "100%",
    border: "default",
    background: "white",
    boxShadow: "panel",
    width: props.width || 236,
    flexDirection: "column",
    position: props.position || "absolute",
    display: "block",
    borderRadius: "default",
    overflow: "hidden",
    zIndex: "popupMenu",
    transition: "none",
  };
})`
  line-height: 30px;
`;

const PopupMenu = forwardRef<HTMLDivElement, MenuProps>(
  (
    {
      source,
      open = true,
      vPos = "floatBelow",
      hPos = "alignLeft",
      width = 236,
      maxHeight = 368,
      close,
      stayOpenOnClick = false,
      clickAwayClose = true,
      children,
      ...props
    },
    ref
  ) => {
    // const [bodyStyle, setBodyStyle] = useState<Record<string, string>>();
    const [position, setPosition] = useState<PositionData>({});
    const contentIdentifier = useRef(factory.genString());
    const [zIndex, setZIndex] = useState(0);
    const isClient = useIsClient();

    const windowSize = useWindowSize();
    useEffect(() => {
      if (!isClient) return;
      function preventScroll() {
        setZIndex(document.body.querySelectorAll(".popup-menu-open").length);
        // Allows all scrolling to happen within the overlay, and saves previous scroll position.
        const body = document.querySelector("body");
        body.style.overflow = "hidden";
      }

      function restoreScroll() {
        // Restores scroll position to state before overlay was opened.
        const body = document.querySelector("body");
        body.style.overflow = null;
      }

      if (open) preventScroll();
      else restoreScroll();

      return () => restoreScroll();
    }, [open, isClient]);

    const calculatePosition = useCallback(() => {
      if (!isClient) return;
      let sourceEl: HTMLElement;
      if (typeof source === "string") {
        try {
          sourceEl = document.querySelector(source);
        } catch {
          // Retry another selector below
        }
        if (!sourceEl) sourceEl = document.querySelector(`[data-popup-source='${source}']`);
      } else {
        sourceEl = source as HTMLElement;
      }

      if (!sourceEl || !sourceEl.getBoundingClientRect) {
        // Something about the test environment causes this querySelector to fail
        // and this log pollutes the test output.
        if (settings.environment !== "test-runner") {
          console.error(
            `Unable to find source element ${source}`,
            sourceEl,
            sourceEl?.getBoundingClientRect
          );
        }
        return;
      }

      const rect = sourceEl.getBoundingClientRect(),
        windowRect = document.body.getBoundingClientRect(),
        contentEl = document.body.querySelector(`#${contentIdentifier.current}`),
        contentRect = contentEl.getBoundingClientRect();

      const transform: { x: string | number; y: string | number } = { x: 0, y: 0 };
      const pos: PositionData = {
        width: Number(width),
      };

      switch (hPos) {
        case "alignLeft":
          if (rect.left + contentRect.width > windowRect.width) {
            pos.right = "s";
          } else {
            pos.left = rect.left;
          }
          break;
        case "alignRight":
          if (rect.left + rect.width < contentRect.width) pos.left = "s";
          else {
            pos.left = rect.left + rect.width;
            transform.x = "-100%";
          }
          break;
        case "center": {
          const center = rect.left + rect.width / 2;
          if (center + contentRect.width / 2 > windowRect.width) {
            pos.right = "s";
          } else if (center < contentRect.width / 2) {
            pos.left = "s";
          } else {
            pos.left = center - contentRect.width / 2;
          }
          break;
        }
        case "floatLeft":
          if (rect.left < contentRect.width) {
            pos.left = "s";
          } else {
            pos.left = rect.left;
            transform.x = "-100%";
          }
          break;
        case "floatRight":
          if (rect.left + rect.width + contentRect.width > windowRect.width) {
            pos.right = "s";
          } else {
            pos.left = rect.left + rect.width;
          }
          break;
        default:
          break;
      }

      switch (vPos) {
        case "alignTop":
          if (rect.top + contentRect.height > windowRect.height) {
            pos.bottom = "s";
          } else {
            pos.top = rect.top;
          }
          break;
        case "alignBottom":
          if (rect.top + rect.height < contentRect.height) {
            pos.top = "s";
          } else {
            pos.top = rect.top + rect.height;
            transform.y = "-100%";
          }
          break;
        case "center": {
          const center = rect.top + rect.height / 2;
          if (center + contentRect.height / 2 > windowRect.height) {
            pos.bottom = "s";
          } else if (center < contentRect.height / 2) {
            pos.top = "s";
          } else {
            pos.top = rect.top;
            transform.y = "-50%";
          }
          break;
        }
        case "floatAbove":
          if (rect.top < contentRect.height) {
            pos.top = "s";
          } else {
            pos.top = rect.top;
            transform.y = "-100%";
          }
          break;
        case "floatBelow":
          if (rect.top + rect.height + contentRect.height > windowRect.height) {
            pos.bottom = "s";
          } else {
            pos.top = rect.top + rect.height;
          }
          break;
        default:
          break;
      }

      pos.transform = `translate(${transform.x}, ${transform.y})`;
      setPosition(pos);
    }, [hPos, source, vPos, width, isClient]);

    useLayoutEffect(() => {
      // ✅ Fix: Only calculate position on client side
      if (!isClient) return;
      if (!open) return;
      calculatePosition();
    }, [hPos, open, vPos, width, windowSize, calculatePosition, isClient]);

    // Recalculate position when content size changes (e.g., options added/removed)
    useLayoutEffect(() => {
      if (!isClient || !open) return;

      const contentEl = document.body.querySelector(`#${contentIdentifier.current}`);
      if (!contentEl) return;

      const resizeObserver = new ResizeObserver(() => {
        calculatePosition();
      });

      resizeObserver.observe(contentEl);

      return () => {
        resizeObserver.disconnect();
      };
    }, [isClient, open, calculatePosition]);

    const noBubble: MouseEventHandler = useCallback(e => e.stopPropagation(), []);

    function renderMenu() {
      return (
        <MenuContainer
          {...position}
          onClick={!stayOpenOnClick ? close : undefined}
          position={clickAwayClose ? "absolute" : "fixed"}
          {...props}
          ref={ref}
          display={open ? undefined : "none"}
          zIndex={50000 + zIndex}
          className={open ? "popup-menu-open" : null}>
          <Flex
            id={contentIdentifier.current}
            flexDirection="column"
            maxHeight={maxHeight}
            overflow="auto"
            data-testid="popup-menu-content">
            {children}
          </Flex>
        </MenuContainer>
      );
    }

    function renderWrappedMenu() {
      return (
        <Box
          display={open ? undefined : "none"}
          zIndex={50000 + zIndex}
          position="fixed"
          top="0"
          bottom="0"
          right="0"
          left="0"
          onClick={noBubble}
          onDoubleClick={noBubble}>
          <Box position="absolute" top="0" bottom="0" right="0" left="0" onClick={close} />
          {renderMenu()}
        </Box>
      );
    }

    if (!isClient) return null;

    return createPortal(clickAwayClose ? renderWrappedMenu() : renderMenu(), document.body);
  }
);

PopupMenu.displayName = "PopupMenu";

export default PopupMenu;
